// Matt's rule tables (docs/requirements/firm-onboarding-intake.md §4 Step 5, §7) as pure functions.
// Shapes: docs/contract.md §3. Every table row has a test in test/rules.test.ts.
import { FUNCTIONS, functionDef, toolLabel } from './catalog.js';
import { anyRowHas, isUnanswered, optionLabel, renewalLabel, } from './answers.js';
// ---------------------------------------------------------------- upload slots
export const SLOT_IDS = [
    'karbon_contacts', 'karbon_work', 'drake_client_lists', 'qbo_customers',
    'ignition_services', 'team_list', 'job_templates', 'anything_else',
];
const SLOT_DEFS = {
    karbon_contacts: { id: 'karbon_contacts', title: 'Karbon contacts export', detail: 'Your people and organizations, exported to Excel.', refreshedAtFreeze: true, countsAsMissing: true },
    karbon_work: { id: 'karbon_work', title: 'Karbon work export', detail: 'Open and scheduled work with status, assignee and due date.', refreshedAtFreeze: true, countsAsMissing: true },
    drake_client_lists: { id: 'drake_client_lists', title: 'Drake client lists', detail: 'One client list per return type you prepare.', refreshedAtFreeze: true, countsAsMissing: true },
    qbo_customers: { id: 'qbo_customers', title: 'QuickBooks Online customer list', detail: 'Your firm’s customer list, exported to Excel.', refreshedAtFreeze: false, countsAsMissing: true },
    ignition_services: { id: 'ignition_services', title: 'Ignition services list', detail: 'The services and prices on your proposals.', refreshedAtFreeze: false, countsAsMissing: true },
    team_list: { id: 'team_list', title: 'Team list', detail: 'Name and email for everyone who needs a login. Roles are set later.', refreshedAtFreeze: false, countsAsMissing: true },
    job_templates: { id: 'job_templates', title: 'Job checklists or templates', detail: 'The spreadsheets or documents you use to run recurring work.', refreshedAtFreeze: false, countsAsMissing: true },
    anything_else: { id: 'anything_else', title: 'Anything else that helps', detail: 'A client spreadsheet, a deadline tracker or a staff list.', refreshedAtFreeze: false, countsAsMissing: false },
};
/** Export guides (source-system playbooks). Null hides "How to export" until a playbook exists. */
export const EXPORT_GUIDES = {};
export function slotDef(id) {
    return { ...SLOT_DEFS[id], exportGuideUrl: EXPORT_GUIDES[id] ?? null, multiple: true };
}
export function uploadSlots(a, rows) {
    const ids = [];
    if (anyRowHas(rows, ['crm', 'work'], 'karbon'))
        ids.push('karbon_contacts', 'karbon_work');
    if (anyRowHas(rows, ['tax'], 'drake'))
        ids.push('drake_client_lists');
    if (anyRowHas(rows, ['books', 'billing'], 'qbo'))
        ids.push('qbo_customers');
    if (anyRowHas(rows, ['el', 'billing'], 'ignition'))
        ids.push('ignition_services');
    ids.push('team_list');
    if (a.templates !== 'none')
        ids.push('job_templates');
    ids.push('anything_else');
    return ids.map(slotDef);
}
// ---------------------------------------------------------------- tips
const TIPS = [
    { fns: ['crm', 'work'], tool: 'karbon', text: 'Good news: we have a proven path for moving firms off Karbon.' },
    { fns: ['email'], tool: 'm365_outlook', text: 'Upstream Portal syncs with Outlook in both directions, so client email stays connected to the client.' },
    { fns: ['books', 'billing'], tool: 'qbo', text: 'Upstream Portal connects to QuickBooks Online.' },
];
export function tipsFor(fn, row) {
    if (!row || row.notApplicable)
        return [];
    return TIPS.filter((t) => t.fns.includes(fn) && row.tools.includes(t.tool)).map((t) => t.text);
}
const t = (toolId, kind, label, detail = null, needsReview = false) => ({ toolId, kind, label, detail, needsReview });
const otherReview = (row) => row.other !== null ? [t('other', 'review', 'Review', 'No playbook', true)] : [];
export function suggestTreatments(fn, row, a) {
    if (row?.notApplicable)
        return [t(null, 'not_applicable', 'N/A')];
    // Time tracking keys off the billing answer, whatever tools were picked.
    if (fn === 'time') {
        if (isUnanswered(row)) {
            return [t(null, 'ask_on_call', 'Ask on call', a.timeBilling === 'no' ? 'Does not bill from time' : null, true)];
        }
        return a.timeBilling === 'yes_need_history'
            ? [t(null, 'migrate', 'Migrate', 'Time history')]
            : [t(null, 'replace', 'Replace', 'Replace going forward')];
    }
    if (isUnanswered(row) || !row)
        return [t(null, 'ask_on_call', 'Ask on call', null, true)];
    switch (fn) {
        case 'crm':
        case 'work':
            return [
                ...row.tools.map((tool) => {
                    if (tool === 'karbon') {
                        return fn === 'work'
                            ? t(tool, 'migrate_at_freeze', 'Migrate', 'Open jobs at freeze date')
                            : t(tool, 'migrate', 'Migrate', 'Karbon playbook');
                    }
                    if (tool === 'spreadsheet')
                        return t(tool, 'migrate', 'Migrate', 'Spreadsheet import');
                    return t(tool, 'migrate', 'Migrate', 'Review: no playbook yet', true);
                }),
                ...otherReview(row),
            ];
        case 'tax':
            return [
                ...row.tools.map((tool) => tool === 'drake'
                    ? t(tool, 'stays_use_data', 'Stays · use data', 'Client lists set entity and form types')
                    : t(tool, 'stays_use_data', 'Stays · use data', 'Review how to get client lists', true)),
                ...otherReview(row),
            ];
        case 'email':
            return [
                ...row.tools.map((tool) => tool === 'm365_outlook'
                    ? t(tool, 'integrate', 'Integrate', 'Outlook two-way sync')
                    : t(tool, 'review', 'Review', 'Confirm support', true)),
                ...otherReview(row),
            ];
        case 'books':
            return [
                ...row.tools.map((tool) => tool === 'qbo'
                    ? t(tool, 'integrate', 'Integrate', 'QuickBooks Online connection')
                    : t(tool, 'review', 'Review', `${toolLabel(tool).replace(/^QuickBooks /, '')} clients`, true)),
                ...otherReview(row),
            ];
        // Row-level ("Any") rules: one treatment for the row; an Other value is surfaced as a flag instead.
        case 'el': {
            const month = typeof a.renewalMonth === 'number' ? renewalLabel(a.renewalMonth) : null;
            return [t(null, 'replace_at_renewal', 'Replace at renewal', month ? `Renewals in ${month}` : 'Existing letters run to renewal')];
        }
        case 'portal':
            return [t(null, 'replace', 'Replace', 'New portal, nothing to migrate')];
        case 'storage':
            return [t(null, 'stays', 'Stays', 'Final documents migrate per scope')];
        case 'chat':
        case 'sched':
        case 'payroll':
            return [t(null, 'stays', 'Stays', a.integrationsText ? 'Check the integrations answer' : null)];
        case 'billing':
            return [t(null, 'review', 'Review', 'Transition plan at renewal', true)];
    }
}
/** A row needs review when any of its treatments does, or when it carries an Other value. */
export function rowNeedsReview(fn, row, a) {
    return (row?.other != null && !row.notApplicable) || suggestTreatments(fn, row, a).some((x) => x.needsReview);
}
export function breakdownSum(a) {
    const b = a.breakdown;
    if (!b)
        return null;
    const vals = [b.individual, b.business, b.trust, b.nonprofit].filter((v) => typeof v === 'number');
    return vals.length ? vals.reduce((x, y) => x + y, 0) : null;
}
/** A function label mid-sentence: "Firm payroll and HR" → "firm payroll and HR". */
const inSentence = (label) => label.charAt(0).toLowerCase() + label.slice(1);
export function flags(a, rows) {
    const out = [];
    if (a.integrationsText?.trim()) {
        out.push({ code: 'integration_will_break', severity: 'warn', label: 'Integration will break', detail: a.integrationsText.trim() });
    }
    // One flag per Other tool, listing every function it was named for, so a tool picked across many rows reads once.
    const others = new Map();
    for (const f of FUNCTIONS) {
        const row = rows[f.id];
        if (row && !row.notApplicable && row.other) {
            const key = row.other.trim().toLowerCase();
            const seen = others.get(key) ?? others.set(key, { name: row.other, fns: [] }).get(key);
            seen.fns.push(inSentence(f.shortLabel));
        }
    }
    for (const { name, fns } of others.values()) {
        out.push({ code: 'tool_without_playbook', severity: 'warn', label: 'Tool with no playbook', detail: `"${name}" (${fns.join(', ')})` });
    }
    if (a.clientList && a.clientList !== 'clean') {
        out.push({
            code: 'data_cleanup', severity: 'warn', label: 'Data cleanup',
            detail: a.clientList === 'some_duplicates' ? 'firm reports some duplicates or old clients' : 'firm says the client list needs a cleanup',
        });
    }
    if (a.fallbackText?.trim()) {
        out.push({ code: 'shadow_tracker', severity: 'warn', label: 'Shadow tracker', detail: a.fallbackText.trim() });
    }
    const unanswered = FUNCTIONS.filter((f) => isUnanswered(rows[f.id]));
    if (unanswered.length) {
        out.push({ code: 'unanswered', severity: 'warn', label: 'Unanswered', detail: unanswered.map((f) => inSentence(f.shortLabel)).join(', ') });
    }
    const sum = breakdownSum(a);
    if (sum !== null && typeof a.clientsTotal === 'number') {
        out.push(sum === a.clientsTotal
            ? { code: 'breakdown_check', severity: 'info', label: 'Breakdown check', detail: `client types add up to ${sum} of ${a.clientsTotal}` }
            : { code: 'breakdown_check', severity: 'warn', label: 'Client count mismatch', detail: `client types add up to ${sum}, firm said about ${a.clientsTotal}` });
    }
    if (a.timeBilling === 'yes_need_history') {
        out.push({ code: 'time_history_in_scope', severity: 'warn', label: 'Time history in scope', detail: 'Kentucky lesson: plan the time-entry migration up front' });
    }
    if (a.targetTiming === 'asap' || a.targetTiming === 'before_tax_season') {
        out.push({ code: 'timeline_tax_season', severity: 'warn', label: 'Timeline', detail: 'set the freeze date around tax season at kickoff' });
    }
    return out;
}
export function rowSourceText(rows, fns, catalogLabels) {
    const labels = [];
    for (const fn of fns) {
        const row = rows[fn];
        if (!row || row.notApplicable)
            continue;
        for (const tool of row.tools)
            labels.push(toolLabel(tool, catalogLabels));
        if (row.other)
            labels.push(row.other);
    }
    const uniq = [...new Set(labels)];
    return uniq.length ? uniq.join(', ') : 'Not answered';
}
const answered = (row) => !!row && !row.notApplicable && !isUnanswered(row);
export function draftReadinessChecklist(a, rows, files = {}, catalogLabels) {
    const src = (fns) => rowSourceText(rows, fns, catalogLabels);
    const fileFor = (slot) => (slot && (files[slot] ?? 0) > 0 ? 'received' : 'needed');
    const crmKarbon = anyRowHas(rows, ['crm'], 'karbon');
    const workKarbon = anyRowHas(rows, ['work'], 'karbon');
    const out = [];
    out.push({ id: 'MR-01', item: 'Firm information and users', applies: 'required', source: 'Intake', treatment: 'Configure', sourceFile: 'from_intake' });
    const contactsSlot = crmKarbon ? 'karbon_contacts' : null;
    out.push({ id: 'MR-02', item: 'Contacts: people', applies: 'required', source: src(['crm']), treatment: 'Migrate', sourceFile: fileFor(contactsSlot) });
    out.push({ id: 'MR-03', item: 'Contacts: organizations', applies: 'required', source: src(['crm']), treatment: 'Migrate', sourceFile: fileFor(contactsSlot) });
    if (a.groups !== 'no') {
        out.push({ id: 'MR-04', item: 'Client groups and relationships', applies: 'required', source: src(['crm']), treatment: 'Migrate', sourceFile: fileFor(contactsSlot) });
    }
    const ignition = anyRowHas(rows, ['el', 'billing'], 'ignition');
    out.push({ id: 'MR-05', item: 'Services', applies: 'required', source: src(['el', 'billing']), treatment: 'Standard catalog', sourceFile: ignition ? fileFor('ignition_services') : 'from_intake' });
    if (a.openJobs === undefined || a.openJobs > 0) {
        out.push({
            id: 'MR-06', item: 'Active and scheduled jobs', applies: a.openJobs === undefined ? 'to_confirm' : 'required',
            source: src(['work']), treatment: workKarbon ? 'Migrate at freeze date' : 'Migrate', sourceFile: fileFor(workKarbon ? 'karbon_work' : null),
        });
    }
    if (a.templates !== 'none') {
        out.push({
            id: 'MR-07', item: 'Job and workflow templates', applies: 'required',
            source: a.templates === 'in_documents' ? 'Spreadsheets or documents' : a.templates === 'in_software' ? src(['work']) : 'Not answered',
            treatment: 'Configure', sourceFile: fileFor('job_templates'),
        });
    }
    if (answered(rows.el)) {
        out.push({ id: 'MR-08', item: 'Engagement letter templates', applies: 'required', source: src(['el']), treatment: 'Replace at renewal', sourceFile: 'after_plan' });
    }
    if (answered(rows.tax)) {
        const drake = anyRowHas(rows, ['tax'], 'drake');
        out.push({ id: 'MR-09', item: 'Tax filings and entity classification', applies: 'required', source: src(['tax']), treatment: drake ? 'Migrate' : 'Review', sourceFile: fileFor(drake ? 'drake_client_lists' : null) });
    }
    if (a.docsScope && a.docsScope.length && !a.docsScope.includes('none')) {
        const years = optionLabel('docsYears', a.docsYears ?? null);
        out.push({
            id: 'MR-10', item: 'Final documents', applies: 'required', source: src(['storage', 'portal']),
            treatment: years ? `Final only, ${years.replace(/^Last /, '').replace('Current year only', 'current year')}` : 'Final only', sourceFile: 'after_plan',
        });
    }
    out.push({ id: 'MR-11', item: 'Email and triage', applies: 'to_confirm', source: src(['email']), treatment: anyRowHas(rows, ['email'], 'm365_outlook') ? 'Integrate' : 'Review', sourceFile: 'decision' });
    out.push({ id: 'MR-12', item: 'Billing, time, proposals and payments', applies: 'to_confirm', source: src(['billing', 'time']), treatment: 'Decide together', sourceFile: 'decision' });
    if (a.fallbackText?.trim()) {
        out.push({ id: 'MR-14', item: 'Anything else the firm relies on', applies: 'to_confirm', source: 'Intake', treatment: 'Decide together', sourceFile: 'decision' });
    }
    if (a.integrationsText?.trim()) {
        out.push({ id: 'MR-15', item: 'Integrations and automations', applies: 'to_confirm', source: 'Intake', treatment: 'Decide together', sourceFile: 'decision' });
    }
    return out;
}
export function migrationScope(a, rows, catalogLabels) {
    const source = new Set();
    const data = new Set();
    let review = 0;
    for (const f of FUNCTIONS) {
        const row = rows[f.id];
        for (const tr of suggestTreatments(f.id, row, a)) {
            if (!tr.toolId || tr.toolId === 'other')
                continue;
            const label = toolLabel(tr.toolId, catalogLabels);
            if (tr.kind === 'migrate' || tr.kind === 'migrate_at_freeze')
                source.add(label);
            if (tr.kind === 'stays_use_data' || tr.kind === 'integrate')
                data.add(label);
        }
        if (rowNeedsReview(f.id, row, a))
            review++;
    }
    // Spreadsheets are an import source, not a system to plan around.
    source.delete(toolLabel('spreadsheet', catalogLabels));
    const docs = a.docsScope?.includes('none')
        ? 'None, starting fresh'
        : a.docsScope?.length
            ? `${optionLabel('docsYears', a.docsYears ?? null) ?? 'Years not given'} · final only`
            : 'Not answered';
    return {
        sourceSystems: [...source],
        dataSystems: [...data],
        records: { clients: a.clientsTotal ?? null, openJobs: a.openJobs ?? null },
        documents: docs,
        timeHistory: a.timeBilling === 'yes_need_history',
        itemsToReview: review,
    };
}
export { functionDef };
//# sourceMappingURL=rules.js.map